/**
 * CTS DEDUPLICATION IN TRADING BARS: keep a signal, then ignore the next four bars.
 *
 * <pre>
 *   a signal is accepted at bar T
 *   -> any signal at T+1, T+2, T+3 or T+4 is folded away
 *   -> the next eligible signal is T+5 or later
 * </pre>
 *
 * <h2>Why this is applied to BARS and not to the dataset</h2>
 *
 * The previous version folded the dataset the moment it was read, grouping timing points within
 * two CALENDAR days. That was too narrow and measured in the wrong unit: a Friday and the
 * following Monday are ONE trading day apart and three calendar days apart, so a weekend walked
 * straight through the window and signals one and two bars apart kept reaching the chart.
 *
 * <p>A bar window cannot be computed at the read. The published file is one row per calendar day
 * from 1801 to 2100 and the reader has no symbol, no exchange and no session list — the same file
 * serves SPY and an Indian equity whose holidays differ, so "bar T+4" has no single meaning there.
 *
 * <p>So the fold happens in `reversalBars`, the one place a CTS date has just been resolved onto
 * the chart's own axis. The engine folds at the equivalent point — `CtsSignalProvider`, which
 * holds the bar history — and both implement the rule in `CtsBarCooldown.java`.
 *
 * <h2>KEEP THE FIRST</h2>
 *
 * "A signal at T, eliminate T+1 through T+4" names the first member as the survivor. This reverses
 * the earlier keep-the-second rule, on instruction.
 */

/** Bars silenced after an accepted signal. MUST equal `CtsBarCooldown.DEFAULT_WINDOW_BARS`. */
export const CTS_COOLDOWN_BARS = 4;

/**
 * Thins hits that are too close together, keeping the first of each run.
 *
 * <h2>Greedy and forward, which is what the rule describes</h2>
 *
 * Each accepted hit silences the next `windowBars` BARS, and the hit that ends a silence is itself
 * accepted and starts a new one. A dense run therefore thins to every sixth bar rather than
 * collapsing to one signal — the stated rule, and worth being explicit about because "dedup a
 * cluster" could also mean "one signal per run however long".
 *
 * @param {Array<{barIndex:number}>} hits sorted by barIndex, one per bar
 * @param {number} windowBars 0 or less keeps everything
 * @returns {Array} a new array containing only the survivors
 */
export function foldCtsHits(hits, windowBars = CTS_COOLDOWN_BARS) {
  if (!Array.isArray(hits) || windowBars <= 0) return hits;

  const kept = [];
  let lastAccepted = null;
  // Sorted defensively: the caller builds these from a Map, and a fold that assumed an order it
  // did not have would silence the wrong members rather than fail.
  [...hits].sort((a, b) => a.barIndex - b.barIndex).forEach((hit) => {
    if (lastAccepted === null || hit.barIndex - lastAccepted > windowBars) {
      kept.push(hit);
      lastAccepted = hit.barIndex;
    }
  });
  return kept;
}
